import { useState, useCallback, useMemo, useRef } from 'react';
import {
  getSpeakersList,
  searchBlocks,
  replaceTextInBlocks,
  replaceAllInBlocks,
  splitBlockBySelection,
} from '../utils/transcriptOperations';
import {
  findBlockIndex,
  findBlockElement,
  extractBlockIdFromElement,
  findBlockById,
  getSelectionCharacterOffsets,
} from '../utils/blockIdMatcher';
import BlockService from '../../../services/transcript/BlockService';
import TranscriptService from '../../../services/transcript/TranscriptService';
import useUndoRedo from './useUndoRedo';

/**
 * useTranscriptState Hook
 * 
 * Main state management for transcript editor
 * Combines block operations with undo/redo functionality
 * 
 * @param {Array} initialBlocks - Initial transcript blocks
 * @returns {Object} State and operation functions
 */
const useTranscriptState = (initialBlocks = [], sessionId = null) => {
  const safeInitialBlocks = Array.isArray(initialBlocks) ? initialBlocks : [];

  const {
    state: blocks,
    pushState,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useUndoRedo(safeInitialBlocks);

  const blocksRef = useRef(blocks);
  blocksRef.current = blocks;

  const [selectedBlocks, setSelectedBlocks] = useState([]);
  const [focusedBlock, setFocusedBlock] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);

  /**
   * Get list of speakers
   */
  const speakers = useMemo(() => getSpeakersList(blocks), [blocks]);

  /**
   * Handle split block at cursor position
   */
  const handleSplit = useCallback(
    (blockId, cursorPosition, currentText = null) => {
      const currentBlocks = blocksRef.current;
      const result = BlockService.splitBlock(currentBlocks, blockId, cursorPosition, currentText);
      if (result.blocks !== currentBlocks) {
        pushState(result.blocks);
        if (result.focusedBlockId) {
          setFocusedBlock(result.focusedBlockId);
        }
      }
    },
    [pushState]
  );

  /**
   * Handle merge block with previous
   */
  const handleMergeUp = useCallback(
    (blockId) => {
      const currentBlocks = blocksRef.current;
      const result = BlockService.mergeUp(currentBlocks, blockId);
      if (result.blocks !== currentBlocks) {
        pushState(result.blocks);
        if (result.focusedBlockId) {
          setFocusedBlock(result.focusedBlockId);
        }
      }
    },
    [pushState]
  );

  /**
   * Handle merge block with next
   */
  const handleMergeDown = useCallback(
    (blockId) => {
      const currentBlocks = blocksRef.current;
      const result = BlockService.mergeDown(currentBlocks, blockId);
      if (result.blocks !== currentBlocks) {
        pushState(result.blocks);
        if (result.focusedBlockId) {
          setFocusedBlock(result.focusedBlockId);
        }
      }
    },
    [pushState]
  );

  /**
   * Handle insert block between two blocks
   */
  const handleInsertBlock = useCallback(
    (afterBlockId) => {
      const currentBlocks = blocksRef.current;
      const result = BlockService.insertBlockBetween(currentBlocks, afterBlockId);
      if (result.blocks !== currentBlocks && result.newBlockId) {
        pushState(result.blocks);
      }
    },
    [pushState]
  );

  /**
   * Handle delete block
   */
  const handleDelete = useCallback(
    (blockId) => {
      const newBlocks = BlockService.delete(blocksRef.current, blockId);
      pushState(newBlocks);
      setSelectedBlocks(prev => prev.filter((id) => id !== blockId));
      setFocusedBlock(prev => prev === blockId ? null : prev);
    },
    [pushState]
  );

  /**
   * Handle duplicate block
   */
  const handleDuplicate = useCallback(
    (blockId) => {
      const currentBlocks = blocksRef.current;
      const result = BlockService.duplicate(currentBlocks, blockId);
      if (result.blocks !== currentBlocks) {
        pushState(result.blocks);
        if (result.focusedBlockId) {
          setFocusedBlock(result.focusedBlockId);
        }
      }
    },
    [pushState]
  );

  /**
   * Handle update block text
   */
  const handleUpdateText = useCallback(
    (blockId, newText, syncedWords = null) => {
      const newBlocks = BlockService.updateText(blocksRef.current, blockId, newText, syncedWords);
      pushState(newBlocks);
    },
    [pushState]
  );

  /**
   * Handle update block speaker
   */
  const handleUpdateSpeaker = useCallback(
    (blockId, newSpeaker) => {
      const newBlocks = BlockService.updateSpeaker(blocksRef.current, blockId, newSpeaker);
      pushState(newBlocks);
    },
    [pushState]
  );

  /**
   * Handle bulk update speakers for selected blocks
   */
  const handleBulkUpdateSpeaker = useCallback(
    (blockIds, newSpeaker) => {
      const newBlocks = BlockService.bulkUpdateSpeaker(blocksRef.current, blockIds, newSpeaker);
      pushState(newBlocks);
    },
    [pushState]
  );

  /**
   * Handle reorder blocks
   */
  const handleReorder = useCallback(
    (fromIndex, toIndex) => {
      const newBlocks = BlockService.reorder(blocksRef.current, fromIndex, toIndex);
      pushState(newBlocks);
    },
    [pushState]
  );

  /**
   * Handle move block up
   */
  const handleMoveUp = useCallback(
    (blockId) => {
      const currentBlocks = blocksRef.current;
      const newBlocks = BlockService.moveUp(currentBlocks, blockId);
      if (newBlocks !== currentBlocks) {
        pushState(newBlocks);
      }
    },
    [pushState]
  );

  /**
   * Handle move block down
   */
  const handleMoveDown = useCallback(
    (blockId) => {
      const currentBlocks = blocksRef.current;
      const newBlocks = BlockService.moveDown(currentBlocks, blockId);
      if (newBlocks !== currentBlocks) {
        pushState(newBlocks);
      }
    },
    [pushState]
  );

  /**
   * Handle focus block
   */
  const handleFocusBlock = useCallback((blockId) => {
    setFocusedBlock(blockId);
  }, []);

  /**
   * Handle blur block
   */
  const handleBlurBlock = useCallback(() => {
    setFocusedBlock(null);
  }, []);

  /**
   * Handle select block
   */
  const handleSelectBlock = useCallback(
    (blockId, multiSelect = false) => {
      if (multiSelect) {
        setSelectedBlocks((prev) =>
          prev.includes(blockId)
            ? prev.filter((id) => id !== blockId)
            : [...prev, blockId]
        );
      } else {
        setSelectedBlocks([blockId]);
      }
    },
    []
  );

  /**
   * Handle select range of blocks
   */
  const handleSelectRange = useCallback(
    (fromBlockId, toBlockId) => {
      const currentBlocks = blocksRef.current;
      const fromIndex = findBlockIndex(currentBlocks, fromBlockId);
      const toIndex = findBlockIndex(currentBlocks, toBlockId);

      if (fromIndex === -1 || toIndex === -1) return;

      const start = Math.min(fromIndex, toIndex);
      const end = Math.max(fromIndex, toIndex);

      const rangeIds = currentBlocks.slice(start, end + 1).map((block) => block.id);
      setSelectedBlocks(rangeIds);
    },
    []
  );

  /**
   * Handle clear selection
   */
  const handleClearSelection = useCallback(() => {
    setSelectedBlocks([]);
  }, []);

  /**
   * Handle search
   */
  const handleSearch = useCallback(
    (query, caseSensitive = false) => {
      setSearchQuery(query);
      const results = searchBlocks(blocksRef.current, query, caseSensitive);
      setSearchResults(results);
      setCurrentSearchIndex(0);
    },
    []
  );

  /**
   * Handle next search result
   */
  const handleNextSearch = useCallback(() => {
    if (searchResults.length === 0) return;
    setCurrentSearchIndex((prev) => (prev + 1) % searchResults.length);
  }, [searchResults]);

  /**
   * Handle previous search result
   */
  const handlePreviousSearch = useCallback(() => {
    if (searchResults.length === 0) return;
    setCurrentSearchIndex(
      (prev) => (prev - 1 + searchResults.length) % searchResults.length
    );
  }, [searchResults]);

  /**
   * Handle replace text
   */
  const handleReplace = useCallback(
    (findText, replaceText, replaceAll = false, caseSensitive = false) => {
      const newBlocks = replaceTextInBlocks(
        blocksRef.current,
        findText,
        replaceText,
        replaceAll,
        caseSensitive
      );
      pushState(newBlocks);

      if (searchQuery) {
        const results = searchBlocks(newBlocks, searchQuery, caseSensitive);
        setSearchResults(results);
      }
    },
    [searchQuery, pushState]
  );

  /**
   * Handle focus next block
   */
  const handleFocusNext = useCallback(() => {
    if (!focusedBlock) return;

    const currentBlocks = blocksRef.current;
    const blockIndex = findBlockIndex(currentBlocks, focusedBlock);
    if (blockIndex === -1 || blockIndex >= currentBlocks.length - 1) return;

    const nextBlock = currentBlocks[blockIndex + 1];
    setFocusedBlock(nextBlock.id);

    setTimeout(() => {
      const element = document.getElementById(`block-content-${nextBlock.id}`);
      element?.focus({ preventScroll: true });
    }, 0);
  }, [focusedBlock]);

  /**
   * Handle focus previous block
   */
  const handleFocusPrevious = useCallback(() => {
    if (!focusedBlock) return;

    const currentBlocks = blocksRef.current;
    const blockIndex = findBlockIndex(currentBlocks, focusedBlock);
    if (blockIndex <= 0) return;

    const previousBlock = currentBlocks[blockIndex - 1];
    setFocusedBlock(previousBlock.id);

    setTimeout(() => {
      const element = document.getElementById(`block-content-${previousBlock.id}`);
      element?.focus({ preventScroll: true });
    }, 0);
  }, [focusedBlock]);

  /**
   * Handle add new speaker
   * If blocks are selected, assign new speaker to them
   * Otherwise, just confirm the speaker was added (will be available in dropdown)
   */
  const handleAddSpeaker = useCallback((newSpeakerName) => {
    if (!newSpeakerName || !newSpeakerName.trim()) {
      return { success: false, error: 'Speaker name is required' };
    }

    // Check if speaker already exists
    const existingSpeaker = speakers.find(s => 
      s.name.toLowerCase() === newSpeakerName.trim().toLowerCase()
    );
    
    if (existingSpeaker) {
      return { success: false, error: 'Speaker already exists' };
    }

    // If there are selected blocks, assign new speaker to them
    if (selectedBlocks.length > 0) {
      const updatedBlocks = blocksRef.current.map(block => {
        if (selectedBlocks.includes(block.id)) {
          return {
            ...block,
            speaker: newSpeakerName.trim(),
            metadata: {
              ...block.metadata,
              isEdited: true,
              lastModified: new Date().toISOString(),
            },
          };
        }
        return block;
      });
      
      pushState(updatedBlocks);
      return { 
        success: true, 
        message: `Speaker "${newSpeakerName}" added and assigned to ${selectedBlocks.length} block(s)`
      };
    }
    
    // If focused block exists, assign to it
    if (focusedBlock) {
      const updatedBlocks = blocksRef.current.map(block => {
        if (block.id === focusedBlock) {
          return {
            ...block,
            speaker: newSpeakerName.trim(),
            metadata: {
              ...block.metadata,
              isEdited: true,
              lastModified: new Date().toISOString(),
            },
          };
        }
        return block;
      });
      
      pushState(updatedBlocks);
      return { 
        success: true, 
        message: `Speaker "${newSpeakerName}" added and assigned to current block`
      };
    }

    // No blocks selected - create a new empty block with this speaker
    const result = BlockService.createEmptyBlock(blocksRef.current, newSpeakerName);
    pushState(result.blocks);
    
    setTimeout(() => {
      const element = document.getElementById(`block-content-${result.newBlockId}`);
      element?.focus({ preventScroll: true });
    }, 100);
    
    return { 
      success: true, 
      message: `Speaker "${newSpeakerName}" added with a new empty block. Start typing to add content.`
    };
  }, [speakers, selectedBlocks, focusedBlock, pushState]);

  /**
   * Handle rename speaker across all blocks
   */
  const handleRenameSpeaker = useCallback(async (oldName, newName) => {
    if (!sessionId) {
      throw new Error('Session ID required for renaming speaker');
    }

    const result = await TranscriptService.renameSpeaker(sessionId, oldName, newName);
    if (!result.success) {
      throw new Error(result.error);
    }
    
    // Update local blocks
    const updatedBlocks = blocksRef.current.map(block => 
      block.speaker === oldName ? { ...block, speaker: newName } : block
    );
    pushState(updatedBlocks);
  }, [sessionId, pushState]);

  /**
   * Handle delete speaker and merge to another
   */
  const handleDeleteSpeaker = useCallback(async (speakerName, mergeToSpeaker) => {
    if (!sessionId) {
      throw new Error('Session ID required for deleting speaker');
    }

    const result = await TranscriptService.deleteSpeaker(sessionId, speakerName, mergeToSpeaker);
    if (!result.success) {
      throw new Error(result.error);
    }
    
    // Update local blocks
    const updatedBlocks = blocksRef.current.map(block =>
      block.speaker === speakerName ? { ...block, speaker: mergeToSpeaker } : block
    );
    pushState(updatedBlocks);
  }, [sessionId, pushState]);

  /**
   * Handle replace all occurrences in all blocks
   */
  const handleReplaceAll = useCallback((searchText, replaceText) => {
    const { blocks: updatedBlocks, count } = replaceAllInBlocks(
      blocksRef.current,
      searchText,
      replaceText
    );

    if (count > 0) {
      pushState(updatedBlocks);
      return { success: true, count };
    }

    return { success: false, count: 0 };
  }, [pushState]);

  /**
   * Capture selection data from a live DOM selection before it can become stale.
   * Call this immediately when the user triggers "assign to speaker" (before modal opens).
   */
  const captureSelectionForAssign = useCallback((selection) => {
    if (!selection || !selection.range || !selection.text) {
      return null;
    }
    try {
      const startNode = selection.range.startContainer;
      const element = startNode.nodeType === Node.TEXT_NODE ? startNode.parentElement : startNode;
      const blockElement = findBlockElement(element);
      if (!blockElement) return null;
      const blockId = extractBlockIdFromElement(blockElement.id);
      if (!blockId) return null;
      const offsets = getSelectionCharacterOffsets(blockElement, selection.range);
      if (!offsets || offsets.startOffset >= offsets.endOffset) return null;
      return {
        blockId,
        startOffset: offsets.startOffset,
        endOffset: offsets.endOffset,
        text: selection.text,
      };
    } catch {
      return null;
    }
  }, []);

  /**
   * Execute assign-to-speaker using pre-captured selection data (no live DOM dependency).
   */
  const handleAssignSelectionToSpeaker = useCallback(
    (capturedData, newSpeaker) => {
      if (!capturedData || !capturedData.blockId || !newSpeaker || !newSpeaker.trim()) {
        return { success: false, error: 'Invalid selection or speaker' };
      }
      const currentBlocks = blocksRef.current;
      const block = findBlockById(currentBlocks, capturedData.blockId);
      if (!block) {
        return { success: false, error: 'Block not found' };
      }
      const newBlocksArray = splitBlockBySelection(
        block,
        capturedData.startOffset,
        capturedData.endOffset,
        newSpeaker.trim()
      );
      const blockIdx = findBlockIndex(currentBlocks, capturedData.blockId);
      if (blockIdx === -1) {
        return { success: false, error: 'Block index not found' };
      }
      const updatedBlocks = [
        ...currentBlocks.slice(0, blockIdx),
        ...newBlocksArray,
        ...currentBlocks.slice(blockIdx + 1),
      ];
      pushState(updatedBlocks);
      return { success: true };
    },
    [pushState]
  );

  return {
    // State
    blocks,
    selectedBlocks,
    focusedBlock,
    speakers,
    searchQuery,
    searchResults,
    currentSearchIndex,

    // Undo/Redo
    undo,
    redo,
    canUndo,
    canRedo,

    // Block operations
    handleSplit,
    handleMergeUp,
    handleMergeDown,
    handleInsertBlock,
    handleDelete,
    handleDuplicate,
    handleUpdateText,
    handleUpdateSpeaker,
    handleBulkUpdateSpeaker,
    handleReorder,
    handleMoveUp,
    handleMoveDown,

    // Focus management
    handleFocusBlock,
    handleBlurBlock,
    handleFocusNext,
    handleFocusPrevious,

    // Selection management
    handleSelectBlock,
    handleSelectRange,
    handleClearSelection,

    // Search & Replace
    handleSearch,
    handleNextSearch,
    handlePreviousSearch,
    handleReplace,

    // Speaker management
    handleAddSpeaker,
    handleRenameSpeaker,
    handleDeleteSpeaker,

    // Text operations
    handleReplaceAll,
    captureSelectionForAssign,
    handleAssignSelectionToSpeaker,
  };
};

export default useTranscriptState;


